import React, { Component } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import { PropTypes } from 'prop-types';
import { reset, change, submit } from 'redux-form';
import { push } from 'connected-react-router';
import NotFoundPage from 'components/Errors/NotFoundPage';
import AccessControl from 'components/AccessControl';
import ActivityPerms from 'components/AccessControl/permissions.json';
import moment from 'moment';

import {
  customerDataLoad,
  customerAccountLink,
  customerAccountUnlink,
  removeCustomerLink,
  clearCustomerData,
  getParentImyForms,
  showDeleteFormModal,
  hideDeleteFormModal,
  deleteForms,
  takeQueueItem,
  hideOwnershipModal,
  deleteSupportingDoc,
  cancelEditCustomer,
  updateCustomer
} from 'actions';

import getCertifictionStatus from 'common/Forms/CertificationStatus';
import getAccountName from 'common/Accounts/GetAccountName';

import LoadingWrapper from 'components/Common/LoadingWrapper';
import AddCustomerAccountForm from 'components/Customer/AddCustomerAccountForm';
import CustomerAccountList from 'components/Customer/CustomerAccountList';
import CustomerFormList from 'components/Customer/CustomerFormList';
import CustomerLinkList from 'components/Customer/CustomerLinkList';
import AddCustomerLinkForm from 'components/Customer/AddCustomerLink';
import WithholdingRates from 'components/WithholdingRates';

import ConfirmModal from 'components/Modals/ConfirmModal';
import ImyFormTable from 'components/ImyFormTable';
import DeleteFormModal from 'components/Modals/DeleteFormModal';
import ChangeOwnershipModal from 'components/Modals/ChangeOwnershipModal';
import ActiveChip from 'components/Common/ActiveChip';
import DirectChip from 'components/Common/DirectChip';
import withContainerErrorBoundary from 'components/ErrorBoundaries/ContainerErrorBoundary/withContainerErrorBoundary';
import MoveForm from 'components/MoveForm';
import CloneForm from 'components/CloneForm';

import ReactTable from 'components/ReactTable';
import supportingDocsColumnDefs from 'components/Customer/CustomerSupportingDocsList/supportingDocsColumnDefs';
import SupportingDocsActions from 'components/Customer/CustomerSupportingDocsList/supportingDocsActions';
import SupportingDocs from 'components/Customer/CustomerSupportingDocsList/supportingDocsUpload';
import { toastr } from 'react-redux-toastr';
import CustomerEditForm from 'components/Customer/CustomerEditForm';

class Customer extends Component {
  static propTypes = {
    permissions: PropTypes.arrayOf(PropTypes.string),
    jurisdictions: PropTypes.arrayOf(PropTypes.shape()),
    settings: PropTypes.shape({
      showWithholding: PropTypes.bool,
      showJointAccounts: PropTypes.bool
    }).isRequired,
    dispatch: PropTypes.func.isRequired,
    match: PropTypes.shape({
      params: PropTypes.shape({
        id: PropTypes.string.isRequired
      }).isRequired
    }).isRequired,
    isLoadingData: PropTypes.bool.isRequired,
    storeCustomer: PropTypes.shape(),
    history: PropTypes.shape({
      push: PropTypes.func
    }),
    customerDataLoadError: PropTypes.shape({
      code: PropTypes.number,
      message: PropTypes.string
    }),
    parentImyForms: PropTypes.shape({}),
    isGettingParentImyForms: PropTypes.bool,
    isDeleteFormModalShown: PropTypes.bool,
    isDeletingForm: PropTypes.bool,
    deleteFormError: PropTypes.string,
    isOwnershipModalShown: PropTypes.bool,
    changeOwnershipItem: PropTypes.shape(),
    customerSupportingDocs: PropTypes.arrayOf(PropTypes.shape()),
    isGettingCustomers: PropTypes.bool,
    initSupportingDocGridCriteria: PropTypes.shape(),
    isCustomerEditSuccess: PropTypes.bool
  };

  static defaultProps = {
    permissions: null,
    jurisdictions: null,
    storeCustomer: {
      name: '',
      accounts: [],
      forms: [],
      address: {}
    },
    history: null,
    customerDataLoadError: null,
    parentImyForms: null,
    isGettingParentImyForms: false,
    isDeleteFormModalShown: false,
    isDeletingForm: false,
    deleteFormError: null,
    isOwnershipModalShown: false,
    changeOwnershipItem: null,
    customerSupportingDocs: null,
    isGettingCustomers: false,
    initSupportingDocGridCriteria: null,
    isCustomerEditSuccess: false
  };

  static getSupportingDocumentSource = (value) => {
    switch (value) {
      case 1: return 'Customer Portal';
      case 2: return 'Back Office';
      case 3: return 'API';
      default: return 'N/A';
    }
  };

  constructor(props) {
    super(props);

    const { initSupportingDocGridCriteria, permissions } = props;

    const supportingDocsGridCriteria = initSupportingDocGridCriteria || {
      columnDefs: supportingDocsColumnDefs(permissions),
      pageIndex: 0,
      sortBy: [],
      filters: [],
      pageCount: 0,
      pageSize: 10,
      itemCount: 0,
      selectedItems: [],
      selectedItem: null
    };

    this.state = {
      isUnlinkAccountModalShown: false,
      isUnlinkingAccount: false,
      unlinkAccountError: null,
      accountId: null,
      formToDelete: null,
      data: [],
      supportingDocsGridCriteria,
      loading: true,
      loadingTime: 500,
      initialLoaded: false,
      dataSource: 'booksAndRecords',
      isEditingCustomer: false,
      isUpdatingCustomer: false,
      showSubmitConfirmModal: false,
      hasCustomerFormData: false,
      selectedAccount: null,
      linkToRemoveId: null
    };
  }

  componentDidMount() {
    const { dispatch, match, permissions } = this.props;
    dispatch(customerDataLoad(match.params.id));
    if (AccessControl.hasPermission(permissions, [ActivityPerms.scannedViewTree])) {
      dispatch(getParentImyForms(match.params.id, null));
    }
  }

  componentDidUpdate(prevProps, prevState) {
    const { dispatch, isCustomerEditSuccess, customerSupportingDocs, storeCustomer } = this.props;
    const { initialLoaded } = this.state;
    if (prevProps.isCustomerEditSuccess === false && isCustomerEditSuccess === true) {
      dispatch(customerDataLoad(storeCustomer?.id));
      this.setState({ isEditingCustomer: false });
    }

    if (customerSupportingDocs !== prevProps.customerSupportingDocs) {
      this.processData(customerSupportingDocs, customerSupportingDocs.length, storeCustomer);
      this.hideLoadingAnimation();
      if (!initialLoaded) {
        // eslint-disable-next-line react/no-did-update-set-state
        this.setState({ initialLoaded: true });
      }
    }

    if (storeCustomer !== prevProps.storeCustomer) {
      const formDataSource = 2;
      const hasFormData = storeCustomer && (storeCustomer.accounts?.some(account => account?.formCertificationData)
            || storeCustomer.addressses?.some(address => address.source === formDataSource)
            || storeCustomer.taxResidencies?.some(jotr => jotr.source === formDataSource));

      this.setState({ hasCustomerFormData: hasFormData });
    }
  }

  componentWillUnmount() {
    const { dispatch } = this.props;
    dispatch(clearCustomerData());
    dispatch(cancelEditCustomer());
  }

  onEditCustomerClick(isEditing) {
    const { dispatch } = this.props;
    if (!isEditing) {
      dispatch(reset('customerEditForm'));
    }
    this.setState({ isEditingCustomer: isEditing });
  }

  onCancelEdit() {
    this.onEditCustomerClick(false);
  }

  onSubmitChanges(dispatch) {
    dispatch(submit('customerEditForm'));
    this.setSubmitConfirmModalVisible(false);
  }

  // eslint-disable-next-line class-methods-use-this
  onSubmitCustomerForm(formData, dispatch) {
    const selectedAccount = [formData?.accounts?.find(account => account?.account?.id === formData?.selectedAccountId)];

    const updatedFormData = {
      customer: formData.customer,
      accounts: selectedAccount,
      permanentAddresses: formData.permanentAddresses,
      mailingAddresses: formData.mailingAddresses,
      taxResidencies: formData.taxResidencies
    };

    updateCustomer(updatedFormData, dispatch);
  }

  onDataSourceChange(e) {
    const { dataSource } = this.state;
    if (dataSource === 'form') {
      this.setState({ dataSource: 'booksAndRecords' });
      return;
    }
    this.setState({ dataSource: 'form' });
    this.setState({ isEditingCustomer: false });
  }

  getCustomerDataByDataSource() {
    const { storeCustomer } = this.props;
    const { dataSource, selectedAccount } = this.state;

    const dataSourceNumber = dataSource === 'form' ? 2 : 1;

    const accounts = storeCustomer.accounts?.reduce((displayAccounts, account) => {
      let certData = dataSource === 'form' ? account?.formCertificationData : account?.importCertificationData;

      if (!certData) {
        certData = {};
      }

      const displayAccount = {
        certificationData: certData,
        account: account.account,
        capacity: account.capacity,
        validFrom: account.validFrom,
        validUntil: account.validUntil,
        capacityString: account.capacityString,
      };

      displayAccounts.push(displayAccount);
      return displayAccounts;
    }, []);

    const initialValues = {
      customer: {
        customerId: storeCustomer.id,
        customerType: storeCustomer.customerType,
        entityType: storeCustomer.entityType,
        emailAddress: storeCustomer.emailAddress,
        phoneNumber: storeCustomer.phoneNumber,
        citizenshipCode: storeCustomer.citizenshipCode,
        dateOfBirth: storeCustomer.dateOfBirth,
        countryOfBirth: storeCustomer.countryOfBirth,
        cityOfBirth: storeCustomer.cityOfBirth,
        previousTaxCountry: storeCustomer.previousTaxCountry,
        isInvestor: storeCustomer.isInvestor,
        customerIdentifier: storeCustomer.customerId,
        name: storeCustomer.name,
        nameParts: storeCustomer.nameParts,
        isActive: storeCustomer.isActive,
        isDirect: storeCustomer.isDirect,
        systemCode: storeCustomer.systemCode,
        irsBNotice: storeCustomer.irsBNotice,
        bNoticeReceivedDate: storeCustomer.bNoticeReceivedDate,
        cNoticeReceivedDate: storeCustomer.cNoticeReceivedDate,
        irsCNotice: storeCustomer.irsCNotice,
        lastUpdated: storeCustomer.lastUpdated,
        certificationStatus: storeCustomer.certificationStatus
      },
      accounts,
      permanentAddresses: storeCustomer.addresses?.filter(address => address.source === dataSourceNumber && address.addressType === 1),
      mailingAddresses: storeCustomer.addresses?.filter(address => address.source === dataSourceNumber && address.addressType === 2),
      taxResidencies: storeCustomer.taxResidencies?.filter(jotr => jotr.source === dataSourceNumber),
      dataSource,
      selectedAccountId: selectedAccount?.id
    };

    return initialValues;
  }

  getCustomerName() {
    const { storeCustomer } = this.props;
    if (storeCustomer === null || (!storeCustomer.customerId && !storeCustomer.name)) {
      return 'N/A';
    }

    let customerName = storeCustomer.customerId;

    if (!customerName) {
      customerName = storeCustomer.name;
    } else if (storeCustomer.name !== null && storeCustomer.name !== customerName) {
      customerName = `${ customerName } - ${ storeCustomer.name }`;
    }

    return customerName;
  }

  openRemoveLinkModal = (linkToRemoveId) => {
    this.setState({ isRemoveLinkModalShown: true, linkToRemoveId });
  };

  closeRemoveLinkModal = () => {
    this.setState({ isRemoveLinkModalShown: false });
  };

  confirmRemoveLink = () => {
    const { linkToRemoveId } = this.state;
    this.processLinkRemove(linkToRemoveId);
    this.setState({ isRemoveLinkModalShown: false });
  };

  processLinkRemove = (linkToRemoveId) => {
    const { dispatch, storeCustomer } = this.props;
    dispatch(removeCustomerLink(linkToRemoveId, storeCustomer.id));
  };

  // eslint-disable-next-line class-methods-use-this
  viewSupportingDocument = (item) => {
    if (item) {
      window.open(`customers/supporting-docs/${ item.itemId }`, '_blank'); // to open new page
      return;
    }
    toastr.error('', 'Sorry, this document cannot be viewed');
  };

  deleteSupportingDocument = (item) => {
    if (item) {
      const { dispatch } = this.props;
      dispatch(deleteSupportingDoc(item.itemId));
    }
  };

  openDeleteFormModal = (item) => {
    const { dispatch } = this.props;
    this.setState({ formToDelete: item });
    dispatch(showDeleteFormModal([item]));
  };

  closeDeleteFormModal = () => {
    const { dispatch } = this.props;
    this.setState({ formToDelete: null });
    dispatch(hideDeleteFormModal());
  };

  processDeleteForms = () => {
    const { dispatch, match } = this.props;
    const { formToDelete } = this.state;
    dispatch(deleteForms([formToDelete.id], () => {
      this.closeDeleteFormModal();
      dispatch(customerDataLoad(match.params.id));
    }));
  };

  openUnlinkAccountModal = (accountId) => {
    this.setState({ isUnlinkAccountModalShown: true, accountId });
  };

  closeUnlinkAccountModal = () => {
    this.setState({ isUnlinkAccountModalShown: false });
  };

  confirmUnlinkAccount = () => {
    const { accountId } = this.state;
    this.processAccountUnlink(accountId);
    this.setState({ isUnlinkAccountModalShown: false });
  };

  closeFromOwnershipModal = (item) => {
    const { dispatch } = this.props;
    dispatch(hideOwnershipModal(item));
  };

  reAssignItem = () => {
    const { dispatch, changeOwnershipItem } = this.props;
    if (changeOwnershipItem != null && changeOwnershipItem.id != null) {
      dispatch(takeQueueItem('form', changeOwnershipItem.id, changeOwnershipItem.accountId));
      dispatch(hideOwnershipModal());
    }
  };

  continueFromOwnershipModal = () => {
    const { dispatch, changeOwnershipItem } = this.props;
    dispatch(hideOwnershipModal());
    let redirectUrl = `/forms/${ changeOwnershipItem.formType.toLowerCase() }/${ changeOwnershipItem.id }`;
    if (changeOwnershipItem.accountId) {
      redirectUrl = `${ redirectUrl }?account=${ changeOwnershipItem.accountId }`;
    }
    dispatch(push(redirectUrl));
  };

  getActions = () => {
    const { permissions } = this.props;

    return SupportingDocsActions({
      permissions,
      deleteSupportingDocument: this.deleteSupportingDocument,
      viewSupportingDocument: this.viewSupportingDocument,
      canDelete: this.canDeleteSupportingDocs()
    });
  };

  hideLoadingAnimation = () => {
    const { loadingTime } = this.state;
    const { isGettingCustomers } = this.props;
    const loadingInterval = setInterval(() => {
      if (!isGettingCustomers) {
        this.setState({ loading: false });
        clearInterval(loadingInterval);
      }
    }, loadingTime);
  };

  setSubmitConfirmModalVisible = status => this.setState({ showSubmitConfirmModal: status });

  processData = (items, itemCount, storeCustomer) => {
    const { supportingDocsGridCriteria } = this.state;
    const data = [];
    if (items != null) {
      items.forEach((item) => {
        const accountsWithCollectableDocs = storeCustomer.accounts
          .filter(x => x.account?.legalEntity?.collectableSupportingDocs !== null && x.account?.legalEntity?.collectableSupportingDocs > 0)
          .map(x => x.account.id);
        const form = storeCustomer.forms?.sort((a, b) => a.accountId - b.accountId)
          .find(x => x?.id === item?.formId && accountsWithCollectableDocs.includes(x?.accountId));
        const datum = {
          itemId: item?.id,
          originalFileName: item?.originalFileName,
          typeDescription: item?.typeDescription,
          uploadDate: (item?.uploadDate === null || item?.uploadDate === '') ? 'N/A' : moment(item.uploadDate).format('MMM DD, YYYY HH:mm:ss'),
          formId: item?.formId,
          linkedForm: form?.formTypeDescription,
          account: getAccountName(form),
          accountId: form?.accountId,
          workflowStageDescription: form?.workflowStageDescription,
          workflowUsername: form?.workflowUsername,
          formType: form?.formType,
          source: Customer.getSupportingDocumentSource(item?.metaData?.source),
          formSignedDate: form?.dateSigned ? moment(form.dateSigned).format('MMM DD, YYYY') : '',
        };
        data.push(datum);
      });
      const pageCount = Math.ceil(itemCount / supportingDocsGridCriteria.pageSize);
      this.setState({ data, supportingDocsGridCriteria: { ...supportingDocsGridCriteria, itemCount, pageCount } });
    }
  };

  setSelectedAccount = (account) => {
    if (account?.isActive !== true) {
      this.setState({ isEditingCustomer: false });
    }

    this.setState({ selectedAccount: account });
  };

  processAccountLink(addedAccountData) {
    const { dispatch, storeCustomer } = this.props;
    dispatch(customerAccountLink(storeCustomer.id, addedAccountData));
    dispatch(reset('addCustomerAccountForm'));
    dispatch(change('addCustomerAccountForm', 'accountNumber', null));
  }

  processAccountUnlink(accountId) {
    const { dispatch, storeCustomer } = this.props;
    dispatch(customerAccountUnlink(storeCustomer.id, accountId));
  }

  canDeleteSupportingDocs() {
    const { storeCustomer } = this.props;
    if (storeCustomer?.forms && storeCustomer.forms.length > 0) {
      return storeCustomer.forms.filter(form => form.workflowStageDescription === 'Complete').length === 0
             && storeCustomer.forms.filter(form => form.workflowStageDescription === 'Complete' && form.validUntil < new Date()).length === 0;
    }
    return true;
  }

  render() {
    const { permissions, isLoadingData, storeCustomer, customerDataLoadError, match,
      settings, parentImyForms, isGettingParentImyForms, dispatch,
      isDeleteFormModalShown, isDeletingForm, deleteFormError,
      isOwnershipModalShown, changeOwnershipItem, jurisdictions
    } = this.props;
    const {
      isUnlinkAccountModalShown,
      isUnlinkingAccount,
      unlinkAccountError,
      formToDelete,
      data,
      supportingDocsGridCriteria,
      loading,
      initialLoaded,
      dataSource,
      isEditingCustomer,
      isUpdatingCustomer,
      showSubmitConfirmModal,
      hasCustomerFormData,
      selectedAccount,
      isRemoveLinkModalShown
    } = this.state;

    const certificationStatus = storeCustomer && getCertifictionStatus(storeCustomer.certificationStatus);
    const isActiveCustomer = storeCustomer?.isActive ?? false;
    const isEditDisabled = !storeCustomer?.isDirect || dataSource === 'form' || !isActiveCustomer || !selectedAccount?.isActive;

    return (
      <div className="container customer-page-panel">
        { customerDataLoadError && (customerDataLoadError.code !== 0
          && <NotFoundPage caption={ customerDataLoadError.message } />
        ) }
        { !customerDataLoadError && (isLoadingData || storeCustomer == null) && <LoadingWrapper loaded={ !isLoadingData && storeCustomer !== null } />}
        { !customerDataLoadError && !isLoadingData && storeCustomer !== null
        && <div className="customer-panel" id="customer-page-panel-wrapper">
          <div className="panel" id="customer-page-customer-form-panel">
            <div className="panel-header">
              <div className="panel-header-items">
                <div className="panel-title" id="customer-page-title">
                  Customer: { this.getCustomerName() }
                  <ActiveChip isActive={ storeCustomer?.isActive } />
                  <DirectChip isDirect={ storeCustomer?.isDirect } />
                </div>
                <div className="panel-item-value" id="customer-active-date">
                  Active Date: {moment(storeCustomer.openDate).format('MMM DD, YYYY')}
                </div>
                <div className="panel-item-value" id="customer-cert-status">
                  Certification Status: {certificationStatus ?? 'N/A'}
                </div>
              </div>
              <div className="customer-actions">
                <span>Data Source:</span>
                <label className="form-radio" id="customer-form-radio-label">
                  <input
                    type="radio"
                    name="dataSource"
                    value="form"
                    checked={ dataSource === 'form' }
                    onChange={ e => this.onDataSourceChange(e) }
                    disabled={ !hasCustomerFormData }
                  />
                  <i className="form-icon" title={ !hasCustomerFormData ? 'No Form data to display.' : '' } id="customer-form-radio" /> Form
                </label>
                <label className="form-radio" id="customer-books-records-radio-label">
                  <input
                    type="radio"
                    name="dataSource"
                    value="booksAndRecords"
                    checked={ dataSource === 'booksAndRecords' }
                    onChange={ e => this.onDataSourceChange(e) }
                  />
                  <i className="form-icon" id="customer-books-records-radio" /> Books and Records
                </label>
                <div className="edit-button-group">
                  <AccessControl allowedPermissions={ [ActivityPerms.customerEdit] }>
                    {!isEditingCustomer
                        && <button
                          type="button"
                          className="btn"
                          onClick={ () => this.onEditCustomerClick(true) }
                          disabled={ isEditDisabled }
                          id="customer-edit-button"
                        >
                          <span className="ag-icon-tick" /><i className="icon icon-2x icon-edit mr-2" />
                          Edit
                        </button>}
                    {isEditingCustomer
                        && <button
                          className="btn btn-secondary"
                          title="Save"
                          type="button"
                          disabled={ isUpdatingCustomer }
                          onClick={ () => this.setSubmitConfirmModalVisible(true) }
                          id="customer-edit-button_confirm"
                        ><i className="icon icon-2x icon-check" />
                        </button>}
                    {showSubmitConfirmModal && <ConfirmModal
                      key="confirmSubmit"
                      isShown={ showSubmitConfirmModal }
                      onConfirm={ () => this.onSubmitChanges(dispatch) }
                      onCancel={ () => this.setSubmitConfirmModalVisible(false) }
                      title="Save Customer Data"
                      message="Are you sure you want to save the Customer Data? Updating this information may trigger Alerts and potential Changes in Circumstances."
                    />}
                    {isEditingCustomer
                        && <button
                          className="btn btn-secondary"
                          title="Cancel"
                          type="button"
                          disabled={ isUpdatingCustomer }
                          onClick={ () => this.onCancelEdit() }
                          id="customer-edit-button_cancel"
                        >
                          <i className="icon icon-2x icon-cross" />
                        </button>}
                  </AccessControl>
                </div>
              </div>
            </div>
            <div className="panel-body">
              <div className="no-shrink">
                <div className="columns">
                  <div className="col-12 panel-item-value">
                    <CustomerEditForm
                      initialValues={ this.getCustomerDataByDataSource() }
                      dataSource={ dataSource }
                      customerId={ storeCustomer?.id }
                      jurisdictions={ jurisdictions }
                      isEditingCustomer={ isEditingCustomer && isActiveCustomer && selectedAccount?.isActive }
                      onAccountDropdownChange={ this.setSelectedAccount }
                      onSubmit={ this.onSubmitCustomerForm }
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="panel" id="customer-page-linked-forms-panel">
            <div className="panel-body">
              <div className="panel-body-caption">
                <h2 id="customer-page-linked-forms-name"><strong>Forms: </strong></h2>
              </div>
              <div>
                <CustomerFormList
                  permissions={ permissions }
                  customer={ storeCustomer }
                  onDeleteForm={ item => this.openDeleteFormModal(item) }
                />
              </div>
              { storeCustomer?.forms && storeCustomer.forms.length > 0 && <div className="columns">
                <div className="col-6 col-sm-12">
                  { AccessControl.hasPermission(permissions, [ActivityPerms.scannedEdit]) && <MoveForm
                    title="Move Form"
                    introText="Moving a form removes it from the current account and associates it with the target account and customer.
                      The form will be automatically identified as pending initial review and require revalidation. You cannot move workflow complete forms."
                    forms={ storeCustomer.forms.filter(f => f.workflowStage !== 100) } // Only Non-Workflow Complete Forms
                    sourceCustomerId={ storeCustomer.id }
                    onSuccess={ () => dispatch(customerDataLoad(storeCustomer.id)) }
                  /> }
                </div>
                <div className="col-6 col-sm-12">
                  { AccessControl.hasPermission(permissions, [ActivityPerms.scannedEdit]) && <CloneForm
                    title="Clone Form"
                    introText="Cloning a form creates and associates a new copy of the form on the target account.&nbsp;
                      Where the legal entity set up differs between accounts, the form will be automatically identified as pending initial review and will require validation."
                    forms={ storeCustomer.forms }
                    onSuccess={ () => dispatch(customerDataLoad(storeCustomer.id)) }
                  /> }
                </div>
              </div> }
            </div>
          </div>
          <div className="panel" id="customer-page-linked-forms-panel">
            <div className="panel-body">
              <div className="panel-body-caption">
                <h2 id="customer-page-linked-customers-name"><strong>Linked Customers: </strong></h2>
              </div>
              <div>
                <CustomerLinkList
                  permissions={ permissions }
                  customer={ storeCustomer }
                  onConfirmLinkRemove={ id => this.openRemoveLinkModal(id) }
                />
              </div>
              <AccessControl allowedPermissions={ [ActivityPerms.accountEdit] }>
                <div className="col-8">
                  <AddCustomerLinkForm
                    fromCustomerId={ storeCustomer.id }
                    isAddingLink={ false }
                  />
                </div>
              </AccessControl>
            </div>
          </div>
          <div className="panel" id="supporting-documents-panel">
            <div className="panel-body">
              <div className="panel-body-caption">
                <h2 id="customer-page-linked-forms-name"><strong>Supporting Documents: </strong></h2>
              </div>
              <ReactTable
                data={ data }
                gridCriteria={ supportingDocsGridCriteria }
                onGridCriteriaChanged={ () => null }
                actions={ this.getActions() }
                loading={ loading }
                initialLoaded={ initialLoaded }
                collapseHeight
                enablePagination={ false }
              />
              <SupportingDocs
                customerId={ storeCustomer.id }
                permissions={ permissions }
                documents={ data }
              />
            </div>
          </div>
          <div className="panel" id="customer-page-accounts-panel">
            <div className="panel-body">
              <div className="panel-body-caption">
                <h2 id="customer-page-accounts-name"><strong>Accounts: </strong></h2>
              </div>
              <div>
                <CustomerAccountList
                  customer={ storeCustomer }
                  permissions={ permissions }
                  jointAccountsEnabled={ settings.showJointAccounts }
                  onConfirmAccountUnlink={ accountId => this.openUnlinkAccountModal(accountId) }
                />
              </div>
              { settings.showJointAccounts && <AccessControl allowedPermissions={ [ActivityPerms.customerEdit] }>
                <div className="panel-body-caption">
                  <h2 id="customer-page-link-account-name"><strong>Link an Account: </strong></h2>
                </div>
                <div className="col-8">
                  <AddCustomerAccountForm onConfirmAccountLink={ updateData => this.processAccountLink(updateData) } />
                </div>
              </AccessControl> }
            </div>
          </div>
          {AccessControl.hasPermission(permissions, [ActivityPerms.scannedViewTree]) && <div className="panel" id="customer-page-imy-trees-panel">
            <div className="panel-body">
              <div className="panel-body-caption">
                <h2 id="customer-page-imy-trees-name"><strong>IMY Trees: </strong></h2>
              </div>
              <div>
                <ImyFormTable ImyForms={ parentImyForms } isGettingImyForms={ isGettingParentImyForms } dispatch={ dispatch } />
              </div>
            </div>
          </div>}
          { settings && settings.showWithholding && AccessControl.hasPermission(permissions, [ActivityPerms.whdViewRates])
            && <div className="panel" id="customer-page-whd-rates-panel">
              <div className="panel-body">
                <div className="panel-body-caption">
                  <h2 id="customer-page-whd-rates-name"><strong>Withholding Rates: </strong></h2>
                </div>
                <div>
                  <WithholdingRates customerId={ match.params.id } />
                </div>
              </div>
            </div> }
          <ConfirmModal
            key="unlinkAccount"
            isShown={ isUnlinkAccountModalShown }
            isPerformingAction={ isUnlinkingAccount }
            onConfirm={ () => this.confirmUnlinkAccount() }
            onCancel={ () => this.closeUnlinkAccountModal() }
            error={ unlinkAccountError }
            title="Unlink"
            message="Are you sure you want to unlink this account?"
          />
          <ConfirmModal
            key="RemoveLink"
            isShown={ isRemoveLinkModalShown }
            // isPerformingAction={ isRemovingControllingPerson }
            onConfirm={ () => this.confirmRemoveLink() }
            onCancel={ () => this.closeRemoveLinkModal() }
            title="Unlink"
            message="Are you sure you want to unlink this customer?"
            confirmTitle="Yes"
            cancelTitle="No"
          />
          <DeleteFormModal
            isShown={ isDeleteFormModalShown }
            isDeletingForm={ isDeletingForm }
            onConfirmDelete={ () => this.processDeleteForms() }
            onCancel={ () => this.closeDeleteFormModal() }
            deleteFormError={ deleteFormError }
            items={ formToDelete !== null ? [formToDelete] : [] }
          />
          { changeOwnershipItem != null && <ChangeOwnershipModal
            isShown={ isOwnershipModalShown }
            onAssign={ () => this.reAssignItem() }
            onContinueWithoutAssign={ () => this.continueFromOwnershipModal() }
            onCancel={ () => this.closeFromOwnershipModal() }
            workflowRecord={ changeOwnershipItem }
          /> }
        </div> }
      </div>
    );
  }
}

function mapStateToProps(state) {
  const {
    auth: {
      permissions,
      settings
    },
    common: {
      jurisdictions
    },
    customer: {
      storeCustomer,
      isLoadingData,
      isCustomerIdChangeModalShown,
      returnedExistingCustomerId,
      customerDataLoadError,
      customerSupportingDocs,
      isSubmittingCustomer,
      isCustomerEditSuccess
    },
    forms: {
      isGettingParentImyForms,
      parentImyForms,
      isDeleteFormModalShown,
      isDeletingForm,
      deleteFormError,
      supportingDocs,
      supportingDocsGridCriteria
    },
    workflow: {
      isOwnershipModalShown,
      changeOwnershipItem,
      isGettingCustomers
    }
  } = state;
  return {
    permissions,
    jurisdictions,
    storeCustomer,
    customerSupportingDocs,
    isLoadingData,
    isCustomerIdChangeModalShown,
    returnedExistingCustomerId,
    customerDataLoadError,
    settings,
    isGettingParentImyForms,
    parentImyForms,
    isDeleteFormModalShown,
    isDeletingForm,
    deleteFormError,
    isOwnershipModalShown,
    changeOwnershipItem,
    supportingDocs,
    isGettingCustomers,
    initSupportingDocGridCriteria: supportingDocsGridCriteria,
    isSubmittingCustomer,
    isCustomerEditSuccess
  };
}

const mapDispatchToProps = dispatch => ({ dispatch });

export default withRouter(connect(
  mapStateToProps,
  mapDispatchToProps
)(withContainerErrorBoundary(Customer, 'Customer')));
